import { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import { LanguageProvider } from './contexts/LanguageContext';
import { Home } from './components/home/Home';
import Listings, { Car } from './components/Listings';
import DetailedAd from './components/DetailedAd';
import BuyingForm from './components/BuyingForm';
import AboutUs from './components/AboutUs';
import HowItWorks from './components/HowItWorks';
import ContactModal from './components/ContactModal';

import TermsAndConditionsModal from './components/TermsAndConditionsModal';
import PrivacyPolicyModal from './components/PrivacyPolicyModal';

// Dealership components
import { CarListing } from './types/dealership';
import { LoginPage } from './components/dealership/LoginPage';
import { NavigationBar } from './components/dealership/NavigationBar';
import { ContactUsModal } from './components/dealership/ContactUsModal';
import { CurrentListingsSection } from './components/dealership/CurrentListingsSection';
import { DetailedAdView } from './components/dealership/DetailedAdView';
import { DealershipProfileForm } from './components/dealership/DealershipProfileForm';
import { AddNewCarsSection } from './components/dealership/AddNewCarsSection';
import { Button } from './components/ui/button';

function mapListingLocation(listing: any): { country: string; stateProvince: string; city: string } {
  const defaultState = listing?.dealer_state || 'Unknown';
  const defaultCountry =
    listing?.dealer_country ||
    (defaultState === 'Quebec' || defaultState === 'Ontario' ? 'Canada' : 'USA');

  if (typeof listing?.location === 'string' && listing.location.trim()) {
    const parts = listing.location.split(',').map((p: string) => p.trim());
    return {
      city: parts[0] || listing?.dealer_city || 'Unknown',
      stateProvince: parts[1] || listing?.dealer_state || 'Unknown',
      country: parts[2] || listing?.dealer_country || defaultCountry,
    };
  }

  if (listing?.location && typeof listing.location === 'object') {
    return {
      city: listing.location.city || listing?.dealer_city || 'Unknown',
      stateProvince:
        listing.location.stateProvince || listing.location.state || listing?.dealer_state || 'Unknown',
      country: listing.location.country || listing?.dealer_country || defaultCountry,
    };
  }

  return {
    city: listing?.dealer_city || 'Unknown',
    stateProvince: listing?.dealer_state || 'Unknown',
    country: defaultCountry,
  };
}

const KNOWN_VEHICLE_TYPES = new Set([
  'sedan', 'suv', 'hatchback', 'truck', 'coupe', 'convertible', 'wagon', 'van', 'minivan', 'crossover',
]);

function getDisplayType(listing: any): string {
  const rawType =
    listing?.vehicle_type == null
      ? ''
      : String(listing.vehicle_type).trim();
  if (rawType && KNOWN_VEHICLE_TYPES.has(rawType.toLowerCase())) {
    return rawType;
  }
  const make =
    listing?.make == null ? '' : String(listing.make).trim();
  return make || rawType || 'Sedan';
}

// Client routes component
function ClientRoutes() {
  const navigate = useNavigate();
  const location = useLocation();
  const [showContactModal, setShowContactModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  
  // Scroll position management
  const scrollPositions = useRef<Record<string, number>>({});
  const isNavigatingBack = useRef(false);


  // Scroll position management
  useEffect(() => {
    const saveScroll = () => {
      scrollPositions.current[location.pathname] = window.scrollY;
    };

    const restoreScroll = () => {
      const saved = scrollPositions.current[location.pathname] || 0;
      setTimeout(() => window.scrollTo(0, saved), 0);
    };

    // Save scroll position before navigation
    window.addEventListener('beforeunload', saveScroll);
    
    // Restore scroll position after navigation
    if (isNavigatingBack.current) {
      restoreScroll();
      isNavigatingBack.current = false;
    } else {
      window.scrollTo(0, 0);
    }

    return () => {
      window.removeEventListener('beforeunload', saveScroll);
    };
  }, [location.pathname]);

  const handleNavigate = (page: string) => {
    if (page === 'dealer') {
      navigate('/dealer');
      return;
    }
    
    isNavigatingBack.current = false;
    const routes: Record<string, string> = {
      'home': '/',
      'listings': '/listings',
      'about': '/about',
      'how-it-works': '/how-it-works',
    };
    
    if (routes[page]) {
      navigate(routes[page]);
    }
  };

  const handleCarSelect = (car: Car) => {
    isNavigatingBack.current = false;
    navigate(`/car/${car.id}`);
  };

  const handleCarBuy = (car: Car) => {
    isNavigatingBack.current = false;
    navigate(`/car/${car.id}/buy`);
  };

  const handleBackToListings = () => {
    isNavigatingBack.current = true;
    navigate('/listings');
  };

  return (
    <div className="min-h-screen">
      <Routes>
        <Route
          path="/"
          element={
            <Home
              onNavigate={handleNavigate}
              onCarSelect={handleCarSelect}
              onCarBuy={handleCarBuy}
              onContactClick={() => setShowContactModal(true)}
              onOpenTerms={() => setShowTermsModal(true)}
              onOpenPrivacy={() => setShowPrivacyModal(true)}
            />
          }
        />
        <Route
          path="/listings"
          element={
            <Listings
              onNavigate={handleNavigate}
              onCarSelect={handleCarSelect}
              onCarBuy={handleCarBuy}
              onContactClick={() => setShowContactModal(true)}
              onOpenTerms={() => setShowTermsModal(true)}
              onOpenPrivacy={() => setShowPrivacyModal(true)}
            />
          }
        />
        <Route
          path="/about"
          element={
            <AboutUs
              onNavigate={handleNavigate}
              onContactClick={() => setShowContactModal(true)}
              onOpenTerms={() => setShowTermsModal(true)}
              onOpenPrivacy={() => setShowPrivacyModal(true)}
            />
          }
        />
        <Route
          path="/how-it-works"
          element={
            <HowItWorks
              onNavigate={handleNavigate}
              onContactClick={() => setShowContactModal(true)}
              onOpenTerms={() => setShowTermsModal(true)}
              onOpenPrivacy={() => setShowPrivacyModal(true)}
            />
          }
        />
        <Route
          path="/car/:id/buy"
          element={<CarBuyWrapper
            onNavigate={handleNavigate}
            onContactClick={() => setShowContactModal(true)}
            onOpenTerms={() => setShowTermsModal(true)}
            onOpenPrivacy={() => setShowPrivacyModal(true)}
          />}
        />
        <Route
          path="/car/:id"
          element={<CarDetailWrapper 
            onNavigate={handleNavigate}
            onBackToListings={handleBackToListings}
            onCarBuy={handleCarBuy}
            onContactClick={() => setShowContactModal(true)}
            onOpenTerms={() => setShowTermsModal(true)}
            onOpenPrivacy={() => setShowPrivacyModal(true)}
          />}
        />
      </Routes>

      {/* Modals */}
      <ContactModal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
      />
      
      <TermsAndConditionsModal 
        isOpen={showTermsModal} 
        onClose={() => setShowTermsModal(false)} 
      />
      
      <PrivacyPolicyModal 
        isOpen={showPrivacyModal} 
        onClose={() => setShowPrivacyModal(false)} 
      />
    </div>
  );
}

// Wrapper component to load listing data for edit view
function CarEditWrapper({
  onSave,
  onCancel,
  dealerId
}: {
  onSave: () => void;
  onCancel: () => void;
  dealerId: number;
}) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [listing, setListing] = useState<CarListing | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadListing = async () => {
      if (!id) {
        setError('Listing ID is missing.');
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const { apiService } = await import('./services/api');
        const apiListing = await apiService.getCarListing(Number(id));
        
        // Get car images separately since getCarListing returns images as string[]
        const carImages = await apiService.getCarImages(Number(id));

        // Convert ApiCarListing to CarListing format
        const convertedListing: CarListing = {
          id: apiListing.id.toString(),
          model: `${apiListing.year} ${apiListing.make} ${apiListing.model}`,
          type: apiListing.vehicle_type || 'Sedan',
          price: `$${apiListing.price.toLocaleString()}`,
          creditScore: `${apiListing.min_credit_score || 650}+`,
          sellerInfo: 'Verified Dealer',
          financingTerm: `${apiListing.term_months || 60} Months`,
          carName: `${apiListing.year} ${apiListing.make} ${apiListing.model}`,
          carMake: apiListing.make,
          carModel: apiListing.model,
          carBrand: apiListing.vehicle_type || apiListing.make,
          make: apiListing.make,
          mileage: apiListing.mileage?.toString() || '0',
          fuelType: apiListing.fuel_type || 'gasoline',
          transmission: apiListing.transmission || 'automatic',
          exteriorColor: apiListing.color || 'N/A',
          interiorColor: 'N/A',
          carDescription: apiListing.description || 'No description available',
          msrp: `$${apiListing.price.toLocaleString()}`,
          minCreditScore: apiListing.min_credit_score?.toString() || '650',
          termDuration: apiListing.term_months?.toString() || '60',
          monthlyPayment: apiListing.monthly_payment?.toString() || '0',
          otherRequirements: 'Contact dealer for details',
          carImages: carImages.map(img => ({
            id: img.id,
            imageUrl: img.imageUrl,
            fileName: img.fileName,
            fileSize: img.fileSize,
            isPrimary: img.isPrimary
          })),
          uploadedImageUrls: carImages.map(img => img.imageUrl),
          vin: apiListing.vin || '',
          year: apiListing.year?.toString() || new Date().getFullYear().toString(),
          location: typeof apiListing.location === 'string' 
            ? { country: 'Canada', stateProvince: 'QC', city: apiListing.location } 
            : apiListing.location || { country: 'Canada', stateProvince: 'QC', city: 'Montreal' },
          downPayment: '', // Will be loaded from form data if available
        };
        setListing(convertedListing);
      } catch (err) {
        console.error('Failed to load listing for edit:', err);
        setError('Failed to load listing. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadListing();
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading listing...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen text-red-600">
        <p className="text-lg mb-4">{error}</p>
        <Button onClick={() => navigate('/dealer/listings')}>← Back to Listings</Button>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen text-gray-600">
        <p className="text-lg mb-4">Listing not found.</p>
        <Button onClick={() => navigate('/dealer/listings')}>← Back to Listings</Button>
      </div>
    );
  }

  return (
    <AddNewCarsSection
      editingListing={listing}
      onSave={onSave}
      onCancel={onCancel}
      dealerId={dealerId}
    />
  );
}

// Wrapper component to load car data for detail view
function CarDetailWrapper({ 
  onNavigate, 
  onBackToListings, 
  onCarBuy, 
  onContactClick, 
  onOpenTerms, 
  onOpenPrivacy
}: any) {
  const { id } = useParams<{ id: string }>();
  const [car, setCar] = useState<Car | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Load car data from API
    const loadCar = async () => {
      setLoading(true);
      setError(null);
      try {
        const numericId = Number(id);
        if (!id || Number.isNaN(numericId)) {
          throw new Error('Invalid listing id');
        }

        const { apiService } = await import('./services/api');
        const listing = await apiService.getCarListing(numericId);

        // Listing details should still render even if image endpoint fails.
        let imageUrls: string[] = [];
        try {
          const images = await apiService.getCarImages(numericId);
          imageUrls = images.map(img => img.imageUrl);
        } catch (imageError) {
          console.warn('Failed to load listing images, using listing payload images fallback:', imageError);
          imageUrls = Array.isArray(listing.images) ? listing.images : [];
        }
        
        // Convert CarListing to Car format
        const carData: Car = {
          id: listing.id,
          make: listing.make || '',
          model: listing.model || '',
          year: listing.year || new Date().getFullYear(),
          totalPrice: listing.price || 0,
          monthlyPayment: listing.monthly_payment || 0,
          termMonths: listing.term_months || 72,
          minCreditScore: listing.min_credit_score || 600,
          type: getDisplayType(listing),
          mileage: listing.mileage || 0,
          images: imageUrls,
          description: listing.description || '',
          location: mapListingLocation(listing),
          fuelType: listing.fuel_type || 'Gasoline',
          transmission: listing.transmission || 'Automatic',
          vin: listing.vin || '',
          dealer: listing.dealer_name || 'Unknown Dealer',
          dealerType: listing.dealer_type || undefined,
          downPayment: listing.down_payment || 0,
        };
        setCar(carData);
      } catch (error) {
        console.error('Error loading car:', error);
        setError('Failed to load listing details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadCar();
    }
  }, [id, navigate]);

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  if (error || !car) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen text-gray-700">
        <p className="text-lg mb-4">{error || 'Listing not found'}</p>
        <Button onClick={() => navigate('/listings')}>Back to Listings</Button>
      </div>
    );
  }

  return (
            <DetailedAd
              car={car}
              onNavigate={onNavigate}
              onBackToListings={onBackToListings}
              onCarBuy={onCarBuy}
              onContactClick={onContactClick}
              onOpenTerms={onOpenTerms}
              onOpenPrivacy={onOpenPrivacy}
            />
  );
}

// Wrapper component to load car data for buy view
function CarBuyWrapper({ 
  onNavigate, 
  onContactClick, 
  onOpenTerms, 
  onOpenPrivacy
}: any) {
  const { id } = useParams<{ id: string }>();
  const [car, setCar] = useState<Car | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const loadCar = async () => {
      setLoading(true);
      setError(null);
      try {
        const numericId = Number(id);
        if (!id || Number.isNaN(numericId)) {
          throw new Error('Invalid listing id');
        }

        const { apiService } = await import('./services/api');
        const listing = await apiService.getCarListing(numericId);

        let imageUrls: string[] = [];
        try {
          const images = await apiService.getCarImages(numericId);
          imageUrls = images.map(img => img.imageUrl);
        } catch (imageError) {
          console.warn('Failed to load listing images for buy page, using payload fallback:', imageError);
          imageUrls = Array.isArray(listing.images) ? listing.images : [];
        }
        
        const carData: Car = {
          id: listing.id,
          make: listing.make || '',
          model: listing.model || '',
          year: listing.year || new Date().getFullYear(),
          totalPrice: listing.price || 0,
          monthlyPayment: listing.monthly_payment || 0,
          termMonths: listing.term_months || 72,
          minCreditScore: listing.min_credit_score || 600,
          type: getDisplayType(listing),
          mileage: listing.mileage || 0,
          images: imageUrls,
          description: listing.description || '',
          location: mapListingLocation(listing),
          fuelType: listing.fuel_type || 'Gasoline',
          transmission: listing.transmission || 'Automatic',
          vin: listing.vin || '',
          dealer: listing.dealer_name || 'Unknown Dealer',
          dealerType: listing.dealer_type || undefined,
          downPayment: listing.down_payment || 0,
        };
        setCar(carData);
      } catch (error) {
        console.error('Error loading car:', error);
        setError('Failed to load listing details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadCar();
    }
  }, [id, navigate]);

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  if (error || !car) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen text-gray-700">
        <p className="text-lg mb-4">{error || 'Listing not found'}</p>
        <Button onClick={() => navigate('/listings')}>Back to Listings</Button>
      </div>
    );
  }

  const handleBackToDetail = () => {
    navigate(`/car/${id}`);
  };

  return (
    <BuyingForm
      car={car}
      onNavigate={onNavigate}
      onBackToDetail={handleBackToDetail}
      onContactClick={onContactClick}
      onOpenTerms={onOpenTerms}
      onOpenPrivacy={onOpenPrivacy}
    />
  );
}

// Dealer routes component
function DealerRoutes() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    // Check localStorage on initial render
    const stored = localStorage.getItem('dealer-auth');
    if (stored) {
      try {
        const auth = JSON.parse(stored);
        return !!auth.dealerId;
      } catch {
        return false;
      }
    }
    return false;
  });
  const [currentDealer, setCurrentDealer] = useState<{ id: number; name: string } | null>(() => {
    // Restore dealer from localStorage
    const stored = localStorage.getItem('dealer-auth');
    if (stored) {
      try {
        const auth = JSON.parse(stored);
        return auth.dealerId ? { id: auth.dealerId, name: auth.dealerName || '' } : null;
      } catch {
        return null;
      }
    }
    return null;
  });
  const [activeSection, setActiveSection] = useState('listings');
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [editingListing, setEditingListing] = useState<any>(null);
  const [viewingListing, setViewingListing] = useState<any>(null);
  const [isLoadingListing, setIsLoadingListing] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Sync activeSection with URL and load listing if needed
  useEffect(() => {
    const path = location.pathname;
    if (path === '/dealer/login') {
      setActiveSection('login');
    } else if (path === '/dealer/profile') {
      setActiveSection('profile');
    } else if (path === '/dealer/listings') {
      setActiveSection('listings');
      setViewingListing(null);
      setEditingListing(null);
    } else if (path === '/dealer/listings/new') {
      setActiveSection('addCars');
      setViewingListing(null);
    } else if (path.startsWith('/dealer/listings/')) {
      const id = path.split('/').pop();
      if (id && id !== 'new' && !path.includes('/edit')) {
        // Load listing for view
        setIsLoadingListing(true);
        const loadListing = async () => {
          try {
            const { apiService } = await import('./services/api');
            const apiListing = await apiService.getCarListing(Number(id));
            // Convert API listing to CarListing format
            const convertedListing: CarListing = {
              id: apiListing.id.toString(),
              model: `${apiListing.year} ${apiListing.make} ${apiListing.model}`,
              type: apiListing.vehicle_type || 'Sedan',
              price: `$${apiListing.price.toLocaleString()}`,
              creditScore: `${apiListing.min_credit_score || 650}+`,
              sellerInfo: 'Verified Dealer',
              financingTerm: `${apiListing.term_months || 60} Months`,
              carName: `${apiListing.year} ${apiListing.make} ${apiListing.model}`,
              carMake: apiListing.make,
              carModel: apiListing.model,
              carBrand: apiListing.vehicle_type || apiListing.make,
              make: apiListing.make,
              mileage: apiListing.mileage?.toString() || '0',
              fuelType: apiListing.fuel_type || 'gasoline',
              transmission: apiListing.transmission || 'automatic',
              exteriorColor: apiListing.color || 'N/A',
              interiorColor: 'N/A',
              carDescription: apiListing.description || 'No description available',
              msrp: `$${apiListing.price.toLocaleString()}`,
              minCreditScore: apiListing.min_credit_score?.toString() || '650',
              termDuration: apiListing.term_months?.toString() || '60',
              monthlyPayment: apiListing.monthly_payment?.toString() || '0',
              otherRequirements: 'Contact dealer for details',
              carImages: [],
              uploadedImageUrls: apiListing.images || [],
            };
            setViewingListing(convertedListing);
            setActiveSection('details');
          } catch (error) {
            console.error('Error loading listing:', error);
            setViewingListing(null);
          } finally {
            setIsLoadingListing(false);
          }
        };
        loadListing();
      } else if (id && id !== 'new' && path.includes('/edit')) {
        // Edit mode - handled by CarEditWrapper component
        setActiveSection('addCars');
      }
    }
  }, [location.pathname]);

  const handleLogin = (dealerId: number, dealerName: string) => {
    const dealer = { id: dealerId, name: dealerName };
    setCurrentDealer(dealer);
    setIsLoggedIn(true);
    // Persist to localStorage
    localStorage.setItem('dealer-auth', JSON.stringify({
      dealerId,
      dealerName,
      timestamp: Date.now()
    }));
    navigate('/dealer/listings');
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setCurrentDealer(null);
    setActiveSection('listings');
    setEditingListing(null);
    setViewingListing(null);
    // Clear localStorage
    localStorage.removeItem('dealer-auth');
    navigate('/dealer/login');
  };

  const handleEditListing = (listing: CarListing) => {
    setEditingListing(listing);
    navigate(`/dealer/listings/${listing.id}/edit`);
  };

  const handleViewDetails = (listing: CarListing) => {
    setViewingListing(listing);
    navigate(`/dealer/listings/${listing.id}`);
  };

  const handleBackToListingsDealer = () => {
    setViewingListing(null);
    navigate('/dealer/listings');
  };

  const handleSaveEditedListing = () => {
    setEditingListing(null);
    navigate('/dealer/listings');
    setRefreshTrigger(prev => prev + 1);
  };

  const handleCancelEdit = () => {
    setEditingListing(null);
    navigate('/dealer/listings');
  };

  const handleSectionChange = (section: string) => {
    setActiveSection(section);
    const routes: Record<string, string> = {
      'profile': '/dealer/profile',
      'listings': '/dealer/listings',
      'addCars': '/dealer/listings/new',
    };
    if (routes[section]) {
      navigate(routes[section]);
    }
  };

  // Handle root /dealer path - redirect to login
  useEffect(() => {
    if (location.pathname === '/dealer') {
      navigate('/dealer/login', { replace: true });
    }
  }, [location.pathname, navigate]);

  return (
    <Routes>
      <Route
        path="login"
        element={
          <>
            <LoginPage 
              onLogin={handleLogin}
              onContactClick={() => setIsContactModalOpen(true)}
            />
            <ContactUsModal 
              isOpen={isContactModalOpen} 
              onClose={() => setIsContactModalOpen(false)} 
            />
            <button
              onClick={() => navigate('/')}
              className="fixed top-4 left-4 z-50 bg-white/90 hover:bg-white text-[#f91bdc] px-4 py-2 rounded-lg shadow-lg transition-all duration-200 text-sm font-medium"
            >
              ← Back to Sayaara
            </button>
          </>
        }
      />
      <Route
        path="*"
        element={
          isLoggedIn ? (
            <div className="min-h-screen bg-gray-50">
              <NavigationBar 
                activeSection={activeSection} 
                onSectionChange={handleSectionChange}
                onContactClick={() => setIsContactModalOpen(true)}
                onLogout={handleLogout}
              />
              <button
                onClick={() => navigate('/')}
                className="fixed top-36 left-4 z-40 bg-white hover:bg-gray-50 text-[#f91bdc] px-4 py-2 rounded-lg shadow-lg transition-all duration-200 text-sm font-medium border border-[#f91bdc]/20"
              >
                ← Back to Sayaara
              </button>
              <main className="min-h-screen">
                <Routes>
                  <Route
                    path="profile"
                    element={
                      currentDealer?.id ? (
                        <DealershipProfileForm dealerId={currentDealer.id} />
                      ) : (
                        <div className="flex items-center justify-center min-h-screen">
                          <div className="text-center">
                            <p className="text-lg text-gray-600 mb-4">Unable to load dealer profile</p>
                            <Button onClick={() => navigate('/dealer/login')}>
                              Return to Login
                            </Button>
                          </div>
                        </div>
                      )
                    }
                  />
                  <Route
                    path="listings"
                    element={
                      <CurrentListingsSection 
                        onEditListing={handleEditListing} 
                        onViewDetails={handleViewDetails}
                        dealerId={currentDealer?.id || 1}
                        refreshTrigger={refreshTrigger}
                      />
                    }
                  />
                  <Route
                    path="listings/new"
                    element={
                      <AddNewCarsSection 
                        editingListing={editingListing}
                        onSave={editingListing ? handleSaveEditedListing : undefined}
                        onCancel={editingListing ? handleCancelEdit : undefined}
                        dealerId={currentDealer?.id || 1}
                      />
                    }
                  />
                  <Route
                    path="listings/:id"
                    element={
                      isLoadingListing ? (
                        <div className="flex items-center justify-center min-h-screen">
                          <div className="text-lg">Loading listing details...</div>
                        </div>
                      ) : viewingListing ? (
                        <DetailedAdView 
                          listing={viewingListing} 
                          onBackToListings={handleBackToListingsDealer}
                          onEditListing={handleEditListing}
                        />
                      ) : (
                        <div className="flex items-center justify-center min-h-screen">
                          <div className="text-center">
                            <p className="text-lg text-gray-600 mb-4">Listing not found</p>
                            <button
                              onClick={() => navigate('/dealer/listings')}
                              className="text-[#f91bdc] hover:underline"
                            >
                              Back to Listings
                            </button>
                          </div>
                        </div>
                      )
                    }
                  />
                  <Route
                    path="listings/:id/edit"
                    element={
                      <CarEditWrapper
                        onSave={handleSaveEditedListing}
                        onCancel={handleCancelEdit}
                        dealerId={currentDealer?.id || 1}
                      />
                    }
                  />
                  <Route path="*" element={<Navigate to="/dealer/listings" replace />} />
                </Routes>
              </main>
              <ContactUsModal 
                isOpen={isContactModalOpen} 
                onClose={() => setIsContactModalOpen(false)} 
              />
            </div>
          ) : (
            <Navigate to="/dealer/login" replace />
          )
        }
      />
    </Routes>
  );
}

function AppContent() {
  return (
    <Router>
      <Routes>
        <Route path="/dealer/*" element={<DealerRoutes />} />
        <Route path="/*" element={<ClientRoutes />} />
      </Routes>
    </Router>
  );
}

export default function App() {
  return (
    <LanguageProvider>
      <AppContent />
    </LanguageProvider>
  );
}
